import { capitalize, upperCase } from "lodash";
import React, { useMemo } from "react";
import { getLabelColorStyle } from "../../../components/labels/utils";
import TruncatedText from "../../../components/TruncatedText";
import { useAppearance } from "../../../context/appearance";
import { useViewport } from "../../../context/viewport";
import { breakpoints } from "../../../utils/constants";
import { PaymentMethod, PaymentTypeKey } from "../../../utils/types";
import useCurrency from "../../../utils/useCurrency";

interface Props {
  paymentMethod: PaymentMethod;
  withoutDefaultLabel?: boolean;
}

const PaymentMethodComponent: React.FC<Props> = ({
  paymentMethod,
  withoutDefaultLabel
}) => {
  const { isDark } = useAppearance();
  const getCurrencyTitle = useCurrency((state) => state.getCurrencyTitle);
  const { width } = useViewport();
  const isMobile = useMemo(() => width < breakpoints.md, [width]);
  const truncatedLength = useMemo(() => (isMobile ? 10 : 30), [isMobile]);
  const { currency, name, primary } = useMemo(
    () => paymentMethod,
    [paymentMethod]
  );

  return (
    <>
      <div className="text-sm font-medium text-gray-900 dark:text-white flex items-center flex-wrap">
        <div className="mr-2">{name}</div>

        <div
          className="mr-2 inline-flex items-center rounded-full font-medium px-3 text-sm"
          style={getLabelColorStyle("#d73aaa", isDark)}
        >
          {getCurrencyTitle(currency)}
        </div>

        {!withoutDefaultLabel && primary && (
          <div
            className="mr-2 inline-flex items-center rounded-full font-medium px-3 text-sm"
            style={getLabelColorStyle("#7057ff", isDark)}
          >
            default
          </div>
        )}
      </div>

      <div className="text-sm font-normal text-gray-900 dark:text-white truncate mt-1">
        {renderExtraPaymentTypeInfo(paymentMethod, truncatedLength)}
      </div>
    </>
  );
};

export default PaymentMethodComponent;

//
// Utils
//

const renderExtraPaymentTypeInfo = (
  paymentMethod: PaymentMethod,
  truncatedLength: number
) => {
  const parsedMetadata = JSON.parse(paymentMethod.metadata || "{}");

  switch (paymentMethod.type) {
    case PaymentTypeKey.walletAddress:
      return (
        <TruncatedText
          text={parsedMetadata.walletAddress}
          label={`${upperCase(paymentMethod.currency)} wallet address`}
          truncateLength={truncatedLength}
        />
      );
    case PaymentTypeKey.paypal:
    case PaymentTypeKey.wiseByEmail:
      return (
        <TruncatedText
          text={parsedMetadata.email}
          label={`${capitalize(paymentMethod.type)} email`}
          truncateLength={truncatedLength}
        />
      );
    case PaymentTypeKey.cashApp:
    case PaymentTypeKey.venmo:
      return (
        <TruncatedText
          text={parsedMetadata.handle}
          label="handle"
          truncateLength={truncatedLength}
        />
      );
    case PaymentTypeKey.other:
      return (
        <>
          {parsedMetadata.otherField}:{" "}
          <TruncatedText
            text={parsedMetadata.otherValue}
            label={parsedMetadata.otherField}
            truncateLength={truncatedLength}
          />
        </>
      );
    case PaymentTypeKey.iban:
      return (
        <div className="flex flex-col">
          <TruncatedText
            text={parsedMetadata.iban}
            label="IBAN"
            truncateLength={truncatedLength}
          />
          {parsedMetadata.bic && (
            <TruncatedText
              text={parsedMetadata.bic}
              label="BIC"
              truncateLength={truncatedLength}
            />
          )}
        </div>
      );
    default:
      return null;
  }
};
